import {
  ThemeProvider,
} from "@aws-amplify/ui-react";
import React, {useCallback, useState, useEffect} from "react";
import {startVerification} from "../../services/faceLiveness";
import { logger } from "../../store/middleware/consoleEventLogger";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {clearNavigationWarnings} from "../../hooks/useNavigationWarning";
import {FormControl, MenuItem, Select} from "@mui/material";
import {images} from "../../assets";
import {
  Box,
  Grid,
  Typography,
  Button,
  Link,
  Paper,
  Container,
} from "@mui/material";
import {CognitoIdentityClient} from "@aws-sdk/client-cognito-identity";
import {fromCognitoIdentityPool} from "@aws-sdk/credential-provider-cognito-identity";
import debugLogger from "../../utils/debug";
import {useLanguage} from "../../contexts/LanguageContext";
import QRCodeSection from "../qrCodeSection";
import Loading from "../loadingScreen";
import useSessionStatusPolling from "../../hooks/useSessionStatusPolling";
import { isMobile } from "../../utils/technicalInfo";

const LivenessDetector = () => {
  const [loading, setLoading] = useState(false);
  const {sessionId} = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {language, setLanguage, t} = useLanguage();
  const [error, setError] = useState(null);
  const [awsSessionId, setAwsSessionId] = useState(null);
  const [redirectUrl, setRedirectUrl] = useState(searchParams.get("redirect_url"));
  const mobile = isMobile();
  const [continueOnPC, setContinueOnPC] = useState(false);
  const fromQrCode = searchParams.get("from_qr_code");

  // Poll for session status to handle cancellation from other device
  useSessionStatusPolling(sessionId, mobile && fromQrCode, language, redirectUrl);

  const credentialsProvider = fromCognitoIdentityPool({
    client: new CognitoIdentityClient({region: process.env.REACT_APP_REGION}),
    identityPoolId: process.env.REACT_APP_IDENTITY_POOL_ID,
  });

  const initiateVerification = useCallback(async () => {
    debugLogger.log('Initiating verification process', {sessionId, language});

    setLoading(true);
    try {
      debugLogger.log('Calling startVerification API', {sessionId});
      const response = await startVerification(sessionId);

      debugLogger.log('startVerification response received', {
        status: response?.status,
        hasAwsSessionId: !!response?.data?.aws_session_id,
        hasRedirectUrl: !!response?.data?.redirect_url
      });

      if (response?.status === 200 || response?.status === 201) {
        const sessionKey = response?.data?.aws_session_id;

        debugLogger.log('Fetching AWS credentials', {identityPoolId: process.env.REACT_APP_IDENTITY_POOL_ID});
        // Fetch actual credentials and log them to debug
        const creds = await credentialsProvider();
        debugLogger.awsCredentials(creds);

        const keys = response?.data;

        if (sessionKey) {
          debugLogger.log('AWS session key received, navigating to process page', {
            sessionKey: '***' + sessionKey.slice(-8),
            redirectUrl: keys.redirect_url
          });

          setAwsSessionId(sessionKey);
          setRedirectUrl(keys.redirect_url);
          debugLogger.navigation('Home', `/process/${sessionId}`, {
            age_limit: 18,
            awsSessionId: '***' + sessionKey.slice(-8),
            language: language,
            redirect_url: keys.redirect_url
          });

          navigate(`/process/${sessionId}`, {
            state: {
              age_limit: 18,
              awsSessionId: sessionKey,
              language: language,
              keys: creds,
              redirect_url: keys.redirect_url,
              fromQR: fromQrCode
            },
          });
        } else {
          debugLogger.error('initiateVerification', new Error('Invalid aws_session_id received'), {response: response?.data});
          logger.error("Invalid aws_session_id received");
          navigate("/failed", {state: {redirect_url: keys?.redirect_url}});
        }
      } else {
        debugLogger.error('initiateVerification', new Error('Unexpected response status'), {
          status: response?.status,
          data: response?.data
        });
        logger.error("Unexpected response status:", response?.status);
        setError(new Error("Verification failed with unexpected status."));
        navigate("/failed", {state: {redirect_url: response?.data?.redirect_url}});
      }
    } catch (error) {
      logger.error("Failed to start verification:", error);
      setError(error);
      navigate("/failed", {state: {redirect_url: redirectUrl}});
    } finally {
      setLoading(false);
    }
  }, [credentialsProvider, navigate, sessionId, language, redirectUrl]);
  return (
    <ThemeProvider>
      {loading ? (
        <Loading text={t.landingPage.cameraAuthorization} redirectUrl={redirectUrl}/>
      ) : (
        <Box sx={{backgroundColor: "#f5f5f7", minHeight: "100vh"}}>
          {/* Language Selector */}
          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            sx={{px: 2, pt: 1, position: "relative", zIndex: 9997}}
          >
            <FormControl size="small" sx={{minWidth: 120}}>
              <Select value={language} onChange={(e) => setLanguage(e.target.value)} displayEmpty>
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="es">Español</MenuItem>
                <MenuItem value="it">Italiano</MenuItem>
                <MenuItem value="fr">Français</MenuItem>
                <MenuItem value="de">Deutsch</MenuItem>
                <MenuItem value="pt">Português</MenuItem>
                <MenuItem value="ru">Русский</MenuItem>
                <MenuItem value="zh">中文</MenuItem>
                <MenuItem value="ja">日本語</MenuItem>
                <MenuItem value="ko">한국어</MenuItem>
                <MenuItem value="ar">العربية</MenuItem>
                <MenuItem value="hi">हिन्दी</MenuItem>
                <MenuItem value="nl">Nederlands</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Logo */}
          <Box display="flex" justifyContent="center" sx={{pt: 1}}>
            <img
              src={images.certageLogo}
              alt="Logo"
              style={{
                width: "min(70vw, 300px)",
                height: "auto",
                maxHeight: "140px",
              }}
            />
          </Box>

          <Container maxWidth="lg" sx={{px: {xs: 1, sm: 2, md: 4}, py: 1}}>
            <Typography
              textAlign="center"
              fontFamily="Inter, sans-serif"
              color="#3F4145"
              fontWeight={800}
              fontSize={{xs: 20, md: 32}}
              lineHeight={{xs: "27.5px", md: "44px"}}
              sx={{mt: {xs: 2, md: 3}}}
            >
              {t.landingPage.title}
            </Typography>

            <Typography
              textAlign="center"
              fontFamily="Inter, sans-serif"
              color="#777"
              fontWeight={600}
              fontSize={{xs: 14, md: 24}}
              lineHeight={{xs: "19.25px", md: "33px"}}
              sx={{mt: {xs: 1, md: 2}}}
            >
              <>
                {t.landingPage.subtitleBefore}
                <Box component="span" sx={{ fontWeight: 800 }}>
                  {t.landingPage.subtitleHighlight}
                </Box>
                {t.landingPage.subtitleAfter}
              </>
            </Typography>

            {/* GRID SECTION */}
            {!mobile && !continueOnPC ? (
              // QR ONLY
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center', py: '28px', width: '100%' }}>
                <Box
                  sx={{
                    background: 'rgba(255,255,255,0.8)',
                    borderRadius: '24px',
                    border: '1px solid rgba(255,255,255,0.5)',
                    boxShadow: '0px 25px 50px 0px rgba(0,0,0,0.25)',
                    px: { xs: 2, md: '33px' },
                    py: '33px',
                    width: 'fit-content',
                    mx: 'auto',
                    mt: {xs: 2, md: 4}
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: '31px', alignItems: 'center' }}>
                    {/* Left: QR Code */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Box
                        sx={{
                          borderRadius: '24px',
                          width: '230px',
                          height: '230px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          background: 'linear-gradient(135deg, rgb(255, 255, 255) 0%, rgb(248, 250, 252) 100%)',
                          border: '2px solid #e2e8f0',
                          boxShadow: '0px 10px 15px 0px rgba(0,0,0,0.1), 0px 4px 6px 0px rgba(0,0,0,0.1)'
                        }}
                      >
                        <QRCodeSection sessionId={sessionId} redirectUrl={redirectUrl} />
                      </Box>
                    </Box>

                    {/* Right: Instructions */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', width: { xs: '100%', md: '450px' } }}>
                      <Box>
                        <Typography
                          sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 700,
                            fontSize: '30px',
                            lineHeight: '36px',
                            letterSpacing: '-0.75px',
                            color: '#1d293d',
                            mb: '12px',
                            textAlign: 'left'
                          }}
                        >
                          {t.landingPage.qrCodeTitle}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 400,
                            fontSize: '16px',
                            lineHeight: '24px',
                            color: '#45556c',
                            textAlign: 'left'
                          }}
                        >
                          {t.landingPage.qrCodeSubtitle}
                        </Typography>
                      </Box>

                      {/* How to scan box */}
                      <Box
                        sx={{
                          borderRadius: '16px',
                          border: '1px solid #f1f5f9',
                          px: '21px',
                          py: '21px',
                          background: 'linear-gradient(160.41deg, rgb(239, 246, 255) 0%, rgb(248, 250, 252) 100%)'
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: '12px' }}>
                          <Box sx={{ width: '20px', height: '20px', mt: '2px', flexShrink: 0 }}>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                              <path d="M14.1667 1.66667H5.83333C4.91286 1.66667 4.16667 2.41286 4.16667 3.33333V16.6667C4.16667 17.5871 4.91286 18.3333 5.83333 18.3333H14.1667C15.0871 18.3333 15.8333 17.5871 15.8333 16.6667V3.33333C15.8333 2.41286 15.0871 1.66667 14.1667 1.66667Z" stroke="#205072" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.66667" />
                              <path d="M10 15H10.0083" stroke="#205072" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.66667" />
                            </svg>
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 700,
                                fontSize: '14px',
                                lineHeight: '20px',
                                color: '#205072',
                                mb: '9px',
                                textAlign: 'left'
                              }}
                            >
                              {t.landingPage.howToScan}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#314158', textAlign: 'left' }}>
                                {t.landingPage.scanStep1}
                              </Typography>
                              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#314158', textAlign: 'left' }}>
                                {t.landingPage.scanStep2}
                              </Typography>
                              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#314158', textAlign: 'left' }}>
                                {t.landingPage.scanStep3}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>

                      {/* Continue button */}
                      <Button
                        onClick={() => setContinueOnPC(true)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '15px',
                          px: '24px',
                          py: '11px',
                          borderRadius: '14px',
                          border: '1px solid #e2e8f0',
                          textTransform: 'none',
                          color: '#62748e',
                          '&:hover': {
                            bgcolor: 'rgba(0, 0, 0, 0.04)',
                            borderColor: '#e2e8f0'
                          }
                        }}
                      >
                        <Box sx={{ width: '16px', height: '16px', flexShrink: 0 }}>
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M13.3333 2H2.66667C1.93029 2 1.33333 2.59695 1.33333 3.33333V10C1.33333 10.7364 1.93029 11.3333 2.66667 11.3333H13.3333C14.0697 11.3333 14.6667 10.7364 14.6667 10V3.33333C14.6667 2.59695 14.0697 2 13.3333 2Z" stroke="#62748E" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33333" />
                            <path d="M5.33333 14H10.6667" stroke="#62748E" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33333" />
                            <path d="M8 11.3333V14" stroke="#62748E" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33333" />
                          </svg>
                        </Box>
                        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px' }}>
                          {t.landingPage.continueOnDevice}
                        </Typography>
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ) : (
              // FULL LANDING
              <Grid
                container
                spacing={{xs: 2, sm: 4}}
                alignItems="center"
                justifyContent="center"
                sx={{mt: {xs: 2, md: 4}}}
              >
                {/* Image Section */}
                <Grid item xs={12} md={6} sx={{order: {xs: 1, md: 2}}}>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      mt: {xs: 2, md: 0},
                    }}
                  >
                    <Box
                      component="img"
                      src={images.certageLandingLogo}
                      alt="Facial Scan"
                      sx={{
                        maxWidth: {xs: "90vw", sm: "70vw", md: "100%"},
                        height: {xs: "220px", sm: "320px", md: "470px"},
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                </Grid>

                {/* Text + Button Section */}
                <Grid item xs={12} md={6} sx={{order: {xs: 2, md: 1}}}>
                  <Typography
                    variant="body1"
                    fontFamily="Inter, sans-serif"
                    color="#777"
                    fontWeight={600}
                    fontSize={{xs: 12, md: 20}}
                    textAlign={"center"}
                    lineHeight={{xs: "19.25px", md: "27.5px"}}
                    sx={{width: "100%", mb: 2}}
                  >
                    {t.landingPage.infoBefore}
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      {t.landingPage.infoHighlight}
                    </Box>
                    {t.landingPage.infoAfter}
                  </Typography>
                  <Paper
                    elevation={0}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center", // changed to center button
                      justifyContent: "center",
                      borderRadius: "14px",
                      border: "1px solid #D4ECFF",
                      background: {xs: "#fff", md: "#FBFDFF"},
                      p: {xs: 2, md: "37px 15px"},
                      boxSizing: "border-box",
                      mb: 2,
                      gap: 2,
                    }}
                  >
                    <Button
                      fullWidth
                      onClick={initiateVerification}
                      sx={{
                        backgroundColor: "#1818de",
                        py: 1.5,
                        width: "70%",
                        mx: "auto",
                        borderRadius: "12px",
                        fontSize: {xs: 20, sm: 24},
                        fontWeight: 600,
                        color: "#fff",
                        "&:hover": {backgroundColor: "#1515b0"},
                      }}
                    >
                      {t.landingPage.startButton}
                    </Button>

                    <Typography
                      variant="caption"
                      color="#777"
                      fontFamily="Inter, sans-serif"
                      fontSize={{xs: "0.7rem", sm: "0.75rem"}}
                      textAlign="center"
                      sx={{width: "100%"}}
                    >
                      {t.landingPage.termsAccept}{" "}
                      <Link
                        onClick={() =>
                          navigate("/terms-and-condition", {state: {language}})
                        }
                        color="primary.text"
                        sx={{cursor: "pointer"}}
                      >
                        {t.landingPage.termsLink}
                      </Link>{" "}
                      {t.landingPage.and}{" "}
                      <Link
                        onClick={() =>
                          navigate("/privacy-policy", {state: {language}})
                        }
                        color="primary.text"
                        sx={{cursor: "pointer"}}
                      >
                        {t.landingPage.privacyLink}
                      </Link>
                      . {t.landingPage.noData}
                    </Typography>
                  </Paper>

                  <Link
                    onClick={() => {
                      clearNavigationWarnings(() => {
                        if (redirectUrl) {
                          window.location.href = redirectUrl;
                        } else {
                          window.history.back();
                        }
                      });
                    }}
                    sx={{
                      mt: 2,
                      textAlign: "center",
                      color: "#A1A1A1",
                      cursor: "pointer",
                      fontSize: "14px",
                      display: "block",
                      textDecoration: "none",
                      "&:hover": {color: "#777"},
                    }}
                  >
                    {t.landingPage.cancel}
                  </Link>
                </Grid>
              </Grid>
            )}
          </Container>
        </Box>
      )}
    </ThemeProvider>
  );
};

export default LivenessDetector;
